"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Loader2 } from "lucide-react";
import {
  Dispatch,
  FC,
  FormEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRefresh } from "react-admin";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AutocompleteLocation from "@/taxi-dispatch/dispatch/components/AutocompleteLocation";
import Map from "@/components/map/Map";
import {
  BaseInputGroup,
  BaseStaticSelect,
  BaseTextArea,
} from "@/components/input/CustomInputs";
import axiosInstance from "@/utils/axiosInstance";
import { DirectionsRenderer } from "@react-google-maps/api";
import { Transfer } from "@/interface/entityInterface";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  title: string;
  mode: string;
  id?: number;
}

const libraries: "places"[] = ["places"];

const TransferCU: FC<Props> = ({ open, setOpen, title, mode, id }) => {
  const refresh = useRefresh();

  const [formProp, setFormProp] = useState({
    pickup_location: "",
    pickup_latitude: 0,
    pickup_longitude: 0,
    dropoff_location: "",
    dropoff_latitude: 0,
    dropoff_longitude: 0,
    vehicle_class: "",
    waiting_hours: "",
    distance: "",
    duration: "",
    price: "",
    price_per_km: 0,
    currency: "",
    transfer_way: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);

  const [pickupData, setPickupData] = useState<{
    text: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [dropoffData, setDropoffData] = useState<{
    text: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [directions, setDirectionsData] =
    useState<google.maps.DirectionsResult | null>(null);

  const pickupLocationRef = useRef<HTMLInputElement | null>(null);
  const dropoffLocationRef = useRef<HTMLInputElement | null>(null);

  const getPickupData = (data: any) => {
    setPickupData(data);
  };

  const getDropoffData = (data: any) => {
    setDropoffData(data);
  };

  const getDirectionsData = (data: any) => {
    setDirectionsData(data);
  };

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const dataToSend = {
      ...formProp,
      pickup_location: pickupData?.text,
      pickup_latitude: pickupData?.lat,
      pickup_longitude: pickupData?.lng,
      dropoff_location: dropoffData?.text,
      dropoff_latitude: dropoffData?.lat,
      dropoff_longitude: dropoffData?.lng,
      type: "fixed",
      distance: Number(directions?.routes[0].legs[0].distance?.value) / 1000,
      duration: directions?.routes[0].legs[0].duration?.text,
    };

    try {
      if (mode === "create") {
        await axiosInstance.post(`/transfers`, dataToSend);
        toast("Transfer added successfully", {
          description: "Your transfer was added successfully!",
        });
      } else {
        if (!id) return;
        await axiosInstance.put(`/transfers/${id}`, dataToSend);
        toast("Transfer edited successfully", {
          description: `Transfer ${id} was added successfully!`,
        });
      }
      setFormProp({
        pickup_location: "",
        pickup_latitude: 0,
        pickup_longitude: 0,
        dropoff_location: "",
        dropoff_latitude: 0,
        dropoff_longitude: 0,
        vehicle_class: "",
        waiting_hours: "",
        distance: "",
        duration: "",
        price: "",
        price_per_km: 0,
        currency: "",
        transfer_way: "",
        notes: "",
      });
      refresh();
    } catch (err) {
      console.log(err);
      toast("An error occured. Please try again");
    } finally {
      setOpen(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    if (mode === "edit") {
      const getTransferDetails = async () => {
        const { data } = await axiosInstance.get<Transfer>(`/transfers/${id}`);
        const {
          pickup_latitude,
          pickup_location,
          pickup_longitude,
          dropoff_latitude,
          dropoff_location,
          dropoff_longitude,
          currency,
          price,
          transfer_way,
          waiting_hours,
          notes,
        } = data;
        setFormProp({
          pickup_latitude,
          pickup_location,
          pickup_longitude,
          dropoff_latitude,
          dropoff_location,
          dropoff_longitude,
          currency,
          transfer_way,
          notes,
          price: String(price),
          waiting_hours: waiting_hours || "",
          distance: "",
          duration: "",
          vehicle_class: "",
          price_per_km: 0,
        });

        getPickupData({
          text: pickup_location,
          lat: pickup_latitude,
          lng: pickup_longitude,
        });
        getDropoffData({
          text: dropoff_location,
          lat: dropoff_latitude,
          lng: dropoff_longitude,
        });
      };
      getTransferDetails();
    }
  }, [id]);

  const calculateRoute = async () => {
    if (!pickupData || !dropoffData) return;

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const results = await directionsService.route({
        origin: { lat: pickupData.lat, lng: pickupData.lng },
        destination: { lat: dropoffData.lat, lng: dropoffData.lng },
        travelMode: google.maps.TravelMode.DRIVING,
      });
      getDirectionsData(results);
    } catch (error) {
      console.error("Error calculating route:", error);
      alert("Failed to calculate route. Please try again.");
    }
  };

  useEffect(() => {
    const distanceInKm = parseFloat(
      directions?.routes[0].legs[0].distance?.text || "0",
    );
    const totalPrice = parseFloat(formProp.price || "0");

    if (
      !isNaN(distanceInKm) &&
      !isNaN(totalPrice) &&
      distanceInKm > 0 &&
      totalPrice > 0
    ) {
      const calculatedPricePerKm =
        Math.floor((totalPrice / distanceInKm) * 100) / 100;
      setFormProp((prev) => ({
        ...prev,
        price_per_km: calculatedPricePerKm,
      }));
    }
  }, [formProp.price, directions]);

  useEffect(() => {
    if (!pickupData || !dropoffData) return;
    calculateRoute();
  }, [pickupData, dropoffData]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTitle className="hidden" />
      <SheetContent
        side={"bottom"}
        className="p-4 sm:p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="grid lg:grid-cols-2 gap-10">
          <Map
            libraries={libraries}
            coordinates={{
              pickup_latitude: pickupData?.lat || 0,
              pickup_longitude: pickupData?.lng || 0,
              dropoff_latitude: dropoffData?.lat || 0,
              dropoff_longitude: dropoffData?.lng || 0,
            }}
          >
            {directions && <DirectionsRenderer directions={directions} />}
          </Map>
          <div className="w-full">
            <div>
              <p className="text-lg sm:text-4xl font-bold">{title}</p>
              <div className="flex items-center gap-x-3">
                {directions && (
                  <>
                    <p>
                      <span className="font-bold">Distance:</span>{" "}
                      {directions.routes[0].legs[0].distance?.text}
                    </p>
                    <p>
                      <span className="font-bold">Duration:</span>{" "}
                      {directions.routes[0].legs[0].duration?.text}
                    </p>
                    {formProp.price && (
                      <p>
                        <span className="font-bold">Price per km:</span>{" "}
                        {formProp.price_per_km} {formProp.currency}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <form
              onSubmit={submitForm}
              className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div className="w-full">
                <AutocompleteLocation
                  placeholder="Pickup location"
                  location={getPickupData}
                  inputRef={pickupLocationRef}
                />
              </div>
              <div className="w-full">
                <AutocompleteLocation
                  placeholder="Dropoff location"
                  location={getDropoffData}
                  inputRef={dropoffLocationRef}
                />
              </div>
              <BaseStaticSelect
                id="vehicle_class"
                value={formProp.vehicle_class}
                setValue={setFormProp}
                label="Vehicle class"
                data={[
                  { label: "Economy", value: "economy" },
                  { label: "Business", value: "business" },
                  { label: "Van", value: "van" },
                  { label: "Bus", value: "bus" },
                ]}
              />
              <BaseInputGroup
                id="price"
                value={formProp.price}
                label="Price"
                placeholder="Transfer price"
                setProp={setFormProp}
                type="number"
                width="w-28"
              >
                <BaseStaticSelect
                  id="currency"
                  value={formProp.currency}
                  setValue={setFormProp}
                  label=""
                  data={[
                    { label: "ALL" },
                    { label: "EUR" },
                    { label: "USD" },
                    { label: "GPB" },
                  ]}
                  placeholder="currency"
                />
              </BaseInputGroup>

              <div className="w-full">
                <Label className="mb-1" htmlFor="waiting_hours">
                  Waiting hours *
                </Label>
                <Select
                  value={formProp.waiting_hours}
                  onValueChange={(value) =>
                    setFormProp((prev) => ({
                      ...prev,
                      waiting_hours: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Waiting hours" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Waiting hours</SelectLabel>
                      {[...Array(16).keys()].map((i) => (
                        <SelectItem key={i} value={`${i} hour(s)`}>
                          {i} hour(s)
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full">
                <Label className="mb-1" htmlFor="transfer_way">
                  Transfer Type *
                </Label>
                <Select
                  value={formProp.transfer_way}
                  onValueChange={(value) =>
                    setFormProp((prev) => ({
                      ...prev,
                      transfer_way: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Transfer Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Transfer Type</SelectLabel>
                      <SelectItem value="one_way">One Way</SelectItem>
                      <SelectItem value="round_trip">Return</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <BaseTextArea
                  id="notes"
                  label="Notes"
                  value={formProp.notes}
                  setProp={setFormProp}
                />
              </div>
              <div className="sm:col-span-2 place-self-end">
                <Button
                  type="button"
                  disabled={loading}
                  className="w-full"
                  onClick={submitForm}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TransferCU;
